/**
 * DealerAgreementsPage — admin dashboard of all signed dealer agreements.
 *
 * Mirrors the commissioning-history page layout: filter bar + result table
 * + Excel export + pagination. Each row links to the signed PDF (active
 * signatures) via /admin/dealers/{customer_id}/signed-agreement.pdf.
 *
 * Route: /admin/dealer-agreements
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuthenticatedDownload } from "@/lib/useAuthenticatedDownload";
import { useT } from "@/lib/i18n";
import AccountMenu from "@/components/AccountMenu";

const STATUS_TONE = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-300",
  superseded: "bg-red-100 text-red-800 border-red-300",
};
const PAGE = 50;

export default function DealerAgreementsPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "active", signed_after: "", signed_before: "" });
  // iter150AZ.60 — authenticated PDF download for the signed agreement.
  const { downloading: pdfDl, download: dlPdf } = useAuthenticatedDownload();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset: page * PAGE };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/admin/dealer-agreements", { params });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load dealer agreements");
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(0); };

  const exportXlsx = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await api.get(`/admin/dealer-agreements/export.xlsx?${params.toString()}`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dealer_agreements_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("dealer_agreements.export_done"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-7xl mx-auto">
        <button data-testid="agreements-back-home" onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title={t("aux.back_home_tooltip")}>
          <ArrowLeft size={18} weight="bold" />
        </button>
        <FileText size={20} weight="fill" className="text-blue-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="agreements-title">
          {t("dealer_agreements.page_title")}
        </h1>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="agreements-export-xlsx" onClick={exportXlsx}
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-bold hover:bg-blue-700">
            ↓ {t("dealer_agreements.export")}
          </button>
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <p className="text-sm text-slate-600 max-w-3xl">{t("dealer_agreements.subtitle")}</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input data-testid="agreements-q" placeholder={t("dealer_agreements.filter_search")}
            value={filters.q} onChange={e => setFilter("q", e.target.value)}
            className="border rounded px-2 py-1.5 text-sm" />
          <select data-testid="agreements-status" value={filters.status}
            onChange={e => setFilter("status", e.target.value)}
            className="border rounded px-2 py-1.5 text-sm">
            <option value="active">{t("dealer_agreements.status_active")}</option>
            <option value="superseded">{t("dealer_agreements.status_superseded")}</option>
            <option value="all">{t("dealer_agreements.status_all")}</option>
          </select>
          <input data-testid="agreements-signed-after" type="date"
            value={filters.signed_after} onChange={e => setFilter("signed_after", e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
            title={t("dealer_agreements.signed_after")} />
          <input data-testid="agreements-signed-before" type="date"
            value={filters.signed_before} onChange={e => setFilter("signed_before", e.target.value)}
            className="border rounded px-2 py-1.5 text-sm"
            title={t("dealer_agreements.signed_before")} />
        </div>

        <div className="bg-white border rounded-md overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="agreements-table">
            <thead className="bg-slate-50 border-b text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">{t("dealer_agreements.col_dealer")}</th>
                <th className="text-left px-3 py-2">{t("dealer_agreements.col_type")}</th>
                <th className="text-left px-3 py-2">{t("dealer_agreements.col_signer")}</th>
                <th className="text-left px-3 py-2">{t("dealer_agreements.col_signed_at")}</th>
                <th className="text-left px-3 py-2">{t("dealer_agreements.col_method")}</th>
                <th className="text-left px-3 py-2">{t("dealer_agreements.col_version")}</th>
                <th className="text-left px-3 py-2">{t("dealer_agreements.col_status")}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && <tr><td colSpan={8} className="p-6 text-center text-sm text-slate-500">{t("aux.loading")}</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-sm text-slate-500">{t("dealer_agreements.empty")}</td></tr>
              )}
              {!loading && items.map(it => (
                <tr key={it.id} data-testid={`agreements-row-${it.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-bold">{it.company_name || "—"}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{it.customer_id}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{it.customer_type || "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-bold">{it.signer_name || "—"}</div>
                    <div className="text-slate-500">{it.signer_title || ""}</div>
                    {it.signer_email && <div className="text-slate-500 text-[11px]">{it.signer_email}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">
                    {it.signed_at ? new Date(it.signed_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs uppercase">{it.signature_method || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono">{it.master_doc_version || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_TONE[it.status] || "bg-slate-100"}`}>
                      {it.status || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {it.status === "active" && (
                      <button type="button"
                        data-testid={`agreements-pdf-${it.id}`}
                        disabled={pdfDl}
                        onClick={() => dlPdf(
                          `/admin/dealers/${encodeURIComponent(it.customer_id)}/signed-agreement.pdf`,
                          `dealer_agreement_${it.customer_id}.pdf`,
                        )}
                        className="text-xs text-blue-700 hover:text-blue-900 font-bold disabled:opacity-60 disabled:cursor-wait bg-transparent border-0 p-0">
                        {pdfDl ? "…" : t("dealer_agreements.view_pdf")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > PAGE && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500" data-testid="agreements-page-info">
              {t("dealer_agreements.page_info", null, {
                from: page * PAGE + 1,
                to: Math.min((page + 1) * PAGE, total),
                total,
              })}
            </span>
            <div className="flex gap-1">
              <button data-testid="agreements-prev" disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="px-2 py-1 border rounded disabled:opacity-40">{t("dealer_agreements.prev")}</button>
              <button data-testid="agreements-next" disabled={(page + 1) * PAGE >= total}
                onClick={() => setPage(p => p + 1)}
                className="px-2 py-1 border rounded disabled:opacity-40">{t("dealer_agreements.next")}</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
